import { BasicAuthServiceHelper } from '@/modules/Auth'
import APIRoutes from './APIRoutes'
import { flattenMapping, invertFlatMapping, transformObject } from './dataTransformer'
import { AnimalData } from '@/components/PetSitter/AddAnimal/AnimalForm'
import { Products } from '@/hooks/useCart'
import { Order } from '@/data/PetSitter/Messages'

export type PetsitterStats = {
  recurringCustomersCount: number
  completedServicesCount: number
  averageResponseTime: string
  averageResponseTimeUnderThreeHours: boolean
}

// Define a mapping between app keys and API keys
const transformationMap = {
  mainPicture: {
    imageUrl: 'mainPicture.fullSizeUrl',
    imageThumbnailUrl: 'mainPicture.thumbnailUrl',
  },
  pictures: [
    {
      imageUrl: 'pictures.fullSizeUrl',
      imageThumbnailUrl: 'pictures.thumbnailUrl',
    },
  ],
  availabilities: [
    {
      start: 'availabilities.startDate',
      end: 'availabilities.endDate',
    },
  ],
}

// Flatten the mapping
export const flatMap = flattenMapping(transformationMap)
export const reverseMap = invertFlatMapping(flatMap)

// Get services data (GET /v2/petsitting/account/services)
export const getProfileAsync = async (id: string, doNotAuthenticate?: boolean) => {
  const { path: urlPath, method } = APIRoutes.GetProfile(id)
  const getProfileResponse = (await BasicAuthServiceHelper.requestAuthenticatedAsync(
    `${urlPath}`,
    method,
    undefined, // body
    undefined, // headers
    doNotAuthenticate, // doNotAuthenticate ✅
  )) as { state: any }

  if (getProfileResponse == null) {
    const error: any = new Error('Empty response from API')
    error.status = 502
    throw error
  }

  return transformObject(getProfileResponse, reverseMap)
}

export const getStatsAsync = async (id: string, doNotAuthenticate?: boolean) => {
  const { path: urlPath, method } = APIRoutes.GetStats(id)
  const getStatsResponse = (await BasicAuthServiceHelper.requestAuthenticatedAsync(
    `${urlPath}`,
    method,
    undefined, // body
    undefined, // headers
    doNotAuthenticate, // doNotAuthenticate ✅
  )) as PetsitterStats | null

  if (getStatsResponse == null) {
    const error: any = new Error('Empty response from API')
    error.status = 502
    throw error
  }

  return getStatsResponse
}

export const getPetAsync = async (id: string) => {
  const { path: urlPath, method } = APIRoutes.GetPet(id)

  const response = await BasicAuthServiceHelper.requestAuthenticatedAsync<AnimalData>(urlPath, method)
  return response
}

type Cart = {
  products: Products[]
  petsitters: string[]
  animals: any[]
  message: string
  allowAutoRebound: boolean
  allowTenders: boolean
} | null

export const getPriceAsync = async (id: string, cart: Cart) => {
  const { path: urlPath, method } = APIRoutes.GetPrice(id)

  if (!cart) {
    const error: any = new Error('Cart is null')
    error.status = 400
    throw error
  }

  const response = await BasicAuthServiceHelper.requestAuthenticatedAsync<Order>(urlPath, method, cart)
  return response
}
